import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAICoach } from '../contexts/AICoachContext';
import { CoachingChatService } from '@shared/services/coachingChatService';
import type { CoachingMessage, CoachingGoal, UserMood, UrgencyLevel } from '@shared/types/coaching-chat.types';
import { getVoiceInstruction, DEFAULT_VOICE_SETTINGS, type VoiceInstructionPreset } from '@shared/constants/voiceInstructions';
import {
  Send,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  X,
  Minimize2,
  Maximize2,
  MoreVertical,
  Smile,
  AlertCircle,
  CheckCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { toast } from '@shared/components/ui/Toaster';

interface AICoachChatProps {
  isOpen: boolean;
  onClose: () => void;
  isModal?: boolean;
  initialGoal?: CoachingGoal;
  initialMessage?: string;
  conversationId?: string | null;
}

export const AICoachChat: React.FC<AICoachChatProps> = ({
  isOpen,
  onClose,
  isModal = true,
  initialGoal,
  initialMessage,
  conversationId: initialConversationId
}) => {
  const { user } = useAuth();
  const { context, refreshContext } = useAICoach();
  const [messages, setMessages] = useState<CoachingMessage[]>([]);
  const [inputMessage, setInputMessage] = useState(initialMessage || '');
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [coachingGoal, setCoachingGoal] = useState<CoachingGoal>(initialGoal || 'general');
  const [userMood, setUserMood] = useState<UserMood>('neutral');
  const [urgencyLevel, setUrgencyLevel] = useState<UrgencyLevel>('medium');
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [quota, setQuota] = useState<any>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadQuota();

      if (initialConversationId) {
        loadConversationHistory(initialConversationId);
      } else if (initialMessage && conversationId === null) {
        handleSendMessage(initialMessage);
      }
    }
  }, [isOpen, user, initialConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadQuota = async () => {
    if (!user) return;
    try {
      const quotaData = await CoachingChatService.getUserQuota(user.id);
      setQuota(quotaData);
    } catch (error) {
      console.error('Error loading quota:', error);
    }
  };

  const loadConversationHistory = async (convId: string) => {
    if (!convId || !user) return;

    try {
      setIsLoadingHistory(true);
      console.log(`Loading conversation history for: ${convId}`);

      const [conversationData, messagesData] = await Promise.all([
        CoachingChatService.getConversation(convId),
        CoachingChatService.getConversationMessages(convId)
      ]);

      if (!conversationData) {
        toast.error('Conversation not found');
        onClose();
        return;
      }

      if (conversationData.user_id !== user.id) {
        toast.error('You do not have access to this conversation');
        onClose();
        return;
      }

      setMessages(messagesData);
      setCoachingGoal(conversationData.coaching_goal || 'general');
      console.log(`Loaded ${messagesData.length} messages from conversation`);

      if (messagesData.length > 0) {
        toast.success('Conversation history loaded');
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
      toast.error('Failed to load conversation history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend || !user) return;

    if (quota && !quota.has_message_quota) {
      toast.error('Monthly message limit reached. Upgrade to continue.');
      return;
    }

    setIsLoading(true);
    const userMessagePreview: Partial<CoachingMessage> = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: textToSend,
      created_at: new Date().toISOString()
    } as CoachingMessage;

    setMessages(prev => [...prev, userMessagePreview as CoachingMessage]);
    setInputMessage('');

    try {
      const response = await CoachingChatService.sendMessage(
        user.id,
        {
          conversationId: conversationId || undefined,
          content: textToSend,
          coachingGoal,
          userMood,
          urgencyLevel
        },
        context || undefined
      );

      if (!conversationId) {
        setConversationId(response.conversationId);
      }

      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== userMessagePreview.id);
        return [...filtered, response.userMessage, response.assistantMessage];
      });

      await loadQuota();

      if (isSpeaking) {
        playAssistantMessage(response.assistantMessage.content, response.assistantMessage.id);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== userMessagePreview.id));
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    if (!quota?.voice_enabled) {
      toast.error('Voice features not available on your plan');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(Math.min(100, average));
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
      };
      updateAudioLevel();

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }
      }

      const recorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAndSend(audioBlob);
        stream.getTracks().forEach(track => track.stop());

        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        setAudioLevel(0);
        setRecordingDuration(0);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingDuration(0);
      toast.success('Recording started - Speak now!');

      recordingTimerRef.current = window.setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (error: any) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else {
        toast.error(`Failed to access microphone: ${error.message}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log(`\ud83d\uded1 Stopping recording after ${recordingDuration} seconds`);
      toast.info('Processing your recording...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  };

  const transcribeAndSend = async (audioBlob: Blob) => {
    const sizeKB = (audioBlob.size / 1024).toFixed(2);
    console.log(`📤 Sending audio for transcription: ${sizeKB}KB, type: ${audioBlob.type}`);

    if (audioBlob.size === 0) {
      console.error('❌ Empty audio blob');
      toast.error('No audio was recorded. Please try again and speak clearly.');
      return;
    }

    if (audioBlob.size < 1000) {
      console.warn(`⚠️ Very small audio blob: ${audioBlob.size} bytes`);
      toast.error('Recording too short. Please speak for at least 1 second.');
      return;
    }

    try {
      setIsTranscribing(true);
      setIsLoading(true);

      console.log('🔊 Calling transcription API...');
      const transcription = await CoachingChatService.transcribeAudio(audioBlob);

      if (!transcription || transcription.trim().length === 0) {
        console.warn('⚠️ Empty transcription received');
        toast.error('No speech detected. Please try again and speak clearly.');
        return;
      }

      console.log(`✅ Transcription received: "${transcription.substring(0, 50)}..."`);
      toast.success('Audio transcribed successfully!');
      setInputMessage(transcription);
      if (user && recordingDuration > 0) {
        await CoachingChatService.trackVoiceUsage(user.id, recordingDuration);
      }

      await handleSendMessage(transcription);
    } catch (error: any) {
      console.error('Error transcribing audio:', error);
      const errorMessage = error.message || 'Failed to transcribe audio';

      if (errorMessage.includes('not configured') || errorMessage.includes('503')) {
        toast.error('Voice transcription is not configured. Please contact support.');
      } else if (errorMessage.includes('does not have access') || errorMessage.includes('403')) {
        toast.error('OpenAI API key lacks Whisper access. Please update your API key configuration.');
      } else if (errorMessage.includes('Rate limit')) {
        toast.error('Service temporarily busy. Please wait a moment and try again.');
      } else if (errorMessage.includes('Invalid') || errorMessage.includes('401')) {
        toast.error('Voice service authentication issue. Please contact support.');
      } else {
        toast.error(`Transcription failed: ${errorMessage}`);
      }
      setInputMessage('');
    } finally {
      setIsTranscribing(false);
      setIsLoading(false);
    }
  };

  const playAssistantMessage = async (text: string, messageId?: string) => {
    if (!text || text.trim().length === 0) {
      console.warn('No text provided for audio synthesis');
      return;
    }

    try {
      setIsPlayingAudio(true);
      setAudioError(null);
      if (messageId) setPlayingMessageId(messageId);

      console.log(`🔊 Synthesizing speech for ${text.length} characters with GPT-4o-mini-tts...`);

      const voicePreset: VoiceInstructionPreset = (quota?.voice_instruction_preset as VoiceInstructionPreset) || 'default';
      const voiceSettings = quota?.voice_settings || DEFAULT_VOICE_SETTINGS;
      const voiceInstruction = getVoiceInstruction(voicePreset);

      const audioBlob = await CoachingChatService.synthesizeSpeech(
        text,
        voiceSettings.voice || 'alloy',
        voiceSettings.speed || 1.0,
        voiceInstruction
      );
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        console.log('✅ Audio playback completed');
        URL.revokeObjectURL(audioUrl);
        setIsPlayingAudio(false);
        setPlayingMessageId(null);
      };

      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsPlayingAudio(false);
        setPlayingMessageId(null);
        setAudioError('Failed to play audio');
        URL.revokeObjectURL(audioUrl);
      };

      await audio.play();
      console.log('▶️ Audio playback started');
      toast.success('Playing audio response');
    } catch (error: any) {
      console.error('Error synthesizing/playing audio:', error);
      setIsPlayingAudio(false);
      setPlayingMessageId(null);

      const errorMessage = error.message || 'Failed to play audio response';
      setAudioError(errorMessage);

      if (errorMessage.includes('not configured')) {
        toast.error('Voice features are not configured. Please contact support.');
      } else if (errorMessage.includes('Rate limit')) {
        toast.error('Voice service temporarily unavailable. Please try again in a moment.');
      } else {
        toast.error('Failed to play audio. Please try again.');
      }
    }
  };

  const toggleSpeaker = () => {
    const newState = !isSpeaking;

    if (!newState && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingAudio(false);
      setPlayingMessageId(null);
    }

    setIsSpeaking(newState);
    setAudioError(null);

    if (newState) {
      toast.success('Voice output enabled - audio will play for new messages');
    } else {
      toast.info('Voice output disabled');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlayingAudio(false);
    setPlayingMessageId(null);
  };

  const handleQuickReply = (reply: string) => {
    setInputMessage(reply);
    handleSendMessage(reply);
  };

  const moodOptions: { value: UserMood; label: string; icon: string }[] = [
    { value: 'confident', label: 'Confident', icon: '😎' },
    { value: 'anxious', label: 'Anxious', icon: '😰' },
    { value: 'frustrated', label: 'Frustrated', icon: '😤' },
    { value: 'motivated', label: 'Motivated', icon: '💪' },
    { value: 'overwhelmed', label: 'Overwhelmed', icon: '😵' },
    { value: 'neutral', label: 'Neutral', icon: '😐' }
  ];

  const goalOptions: { value: CoachingGoal; label: string }[] = [
    { value: 'skills_development', label: 'Skills Development' },
    { value: 'interview_prep', label: 'Interview Prep' },
    { value: 'offer_analysis', label: 'Offer Analysis' },
    { value: 'decision_making', label: 'Decision Making' },
    { value: 'motivation', label: 'Motivation' },
    { value: 'career_planning', label: 'Career Planning' },
    { value: 'general', label: 'General Guidance' }
  ];

  if (!isOpen) return null;

  const chatContent = (
    <div className={`flex flex-col ${isModal ? 'h-full' : 'h-screen'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
            <img
              src="https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=120&h=120&fit=crop"
              alt="Marta - AI Career Coach"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Marta</h3>
            <p className="text-xs opacity-90">
              {initialConversationId && messages.length > 0 ? (
                `Reviewing conversation • ${messages.length} messages`
              ) : (
                `Your AI Career Coach • ${quota ? `${quota.messages_remaining} messages left` : 'Loading...'}`
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isModal && (
            <>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isMinimized ? <Maximize2 className="w-5 h-5" /> : <Minimize2 className="w-5 h-5" />}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Audio Error Banner */}
          {audioError && (
            <div className="bg-red-50 border-b border-red-200 p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800 flex-1">{audioError}</p>
              <button
                onClick={() => setAudioError(null)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Voice Status Banner */}
          {isSpeaking && !audioError && (
            <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-center gap-2">
              <Volume2 className="w-4 h-4 text-blue-600" />
              <p className="text-xs text-blue-800 font-medium">
                Voice output enabled - New messages will be spoken automatically
              </p>
            </div>
          )}

          {/* Controls Bar */}
          <div className="bg-gray-50 border-b p-3 flex items-center gap-3 flex-wrap">
            <select
              value={coachingGoal}
              onChange={(e) => setCoachingGoal(e.target.value as CoachingGoal)}
              className="text-sm border rounded px-2 py-1"
            >
              {goalOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <div className="flex items-center gap-1">
              <Smile className="w-4 h-4 text-gray-500" />
              <select
                value={userMood}
                onChange={(e) => setUserMood(e.target.value as UserMood)}
                className="text-sm border rounded px-2 py-1"
              >
                {moodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.icon} {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {isPlayingAudio && (
                <button
                  onClick={stopAudio}
                  className="p-2 rounded-lg bg-red-100 text-red-600 transition-colors hover:bg-red-200"
                  title="Stop audio"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={toggleSpeaker}
                className={`px-3 py-2 rounded-lg transition-colors font-medium text-sm flex items-center gap-2 ${
                  isSpeaking
                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={isSpeaking ? 'Voice enabled - Click to disable' : 'Voice disabled - Click to enable'}
              >
                {isSpeaking ? (
                  <>
                    <div className="relative">
                      <Volume2 className="w-4 h-4" />
                      {isPlayingAudio && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      )}
                    </div>
                    <span>Sound On</span>
                  </>
                ) : (
                  <>
                    <VolumeX className="w-4 h-4" />
                    <span>Sound Off</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {isLoadingHistory && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">Loading conversation history...</p>
                  <p className="text-sm text-gray-500 mt-2">Please wait while we retrieve your messages</p>
                </div>
              </div>
            )}
            {!isLoadingHistory && messages.length === 0 && (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-100 mx-auto mb-4">
                  <img
                    src="https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=160&h=160&fit=crop"
                    alt="Marta"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2">Hi, I'm Marta!</h3>
                <p className="text-gray-600 mb-4">Your personal AI Career Coach. I'm here to help you grow, navigate challenges, and achieve your career goals. What would you like to work on today?</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
                  {[
                    "What should I focus on first?",
                    "Help me with interview prep",
                    "Review my progress",
                    "Career advice needed"
                  ].map(suggestion => (
                    <button
                      key={suggestion}
                      onClick={() => handleQuickReply(suggestion)}
                      className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm hover:bg-gray-50 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={message.id || index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="whitespace-pre-wrap flex-1">{message.content}</p>
                    {message.role === 'assistant' && (
                      <button
                        onClick={() => playAssistantMessage(message.content, message.id)}
                        disabled={isPlayingAudio && playingMessageId === message.id}
                        className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                          isPlayingAudio && playingMessageId === message.id
                            ? 'bg-blue-100 text-blue-600'
                            : 'hover:bg-gray-100 text-gray-600'
                        }`}
                        title="Play audio"
                      >
                        {isPlayingAudio && playingMessageId === message.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {message.metadata?.quickReplies && message.role === 'assistant' && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                      {message.metadata.quickReplies.map((reply: string, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => handleQuickReply(reply)}
                          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs opacity-70 mt-2">
                    {new Date(message.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}

            {(isLoading || isTranscribing) && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-600">
                      {isTranscribing ? 'Transcribing your message...' : 'Marta is thinking...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="bg-white border-t p-4">
            {isRecording && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-red-900">
                    Recording: {recordingDuration}s - Speak clearly into your microphone
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-500 transition-all duration-100"
                      style={{ width: `${Math.min(100, audioLevel)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
            {isTranscribing && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-blue-900">
                  Transcribing your message... This may take a few seconds.
                </span>
              </div>
            )}
            {quota && !quota.has_message_quota && (
              <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900">Message Limit Reached</p>
                  <p className="text-yellow-700">
                    You've used all {quota.monthly_message_limit} messages this month.
                    Resets on {new Date(quota.period_end).toLocaleDateString()}.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message... (Shift+Enter for new line)"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={2}
                  disabled={isLoading || (quota && !quota.has_message_quota)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim() || isLoading || isTranscribing || (quota && !quota.has_message_quota)}
                  className="p-3"
                >
                  <Send className="w-5 h-5" />
                </Button>
                {quota?.voice_enabled && (
                  <div className="relative">
                    <Button
                      onClick={isRecording ? stopRecording : startRecording}
                      variant={isRecording ? 'destructive' : 'outline'}
                      className="p-3 relative"
                      disabled={isLoading || isTranscribing}
                      title={isRecording ? `Recording: ${recordingDuration}s` : 'Start voice recording'}
                    >
                      {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </Button>
                    {isRecording && (
                      <div className="absolute -top-12 right-0 bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-medium shadow-lg flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-2 h-2 rounded-full bg-white"
                            style={{
                              transform: `scale(${0.5 + (audioLevel / 100) * 0.5})`,
                              transition: 'transform 0.1s'
                            }}
                          />
                          <span>{recordingDuration}s</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className={`bg-white rounded-xl shadow-2xl overflow-hidden ${
          isMinimized ? 'w-80 h-auto' : 'w-full max-w-4xl h-[80vh]'
        }`}>
          {chatContent}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white">
      {chatContent}
    </div>
  );
};
